/*
  ParticlesBackground
  -------------------
  A self-contained, dependency-free canvas particle system used as the
  full-screen background for the auth pages (sign-in / sign-up / reset).

  Design goals:
    - Modern, "tech" feel: dark navy gradient + soft cyan glow blobs in
      the background, white + ice-blue particles drifting up with gentle
      sine wobble, faint constellation lines between near neighbours.
    - High DPI crisp rendering — we scale the canvas by devicePixelRatio
      and re-scale the drawing context on every resize.
    - Cheap: a single 2D canvas, one rAF loop, O(n) particle update,
      neighbour search is O(n²) but n is bounded (~110 particles even on
      large monitors), which keeps mainstream laptops well under 1 ms
      per frame.
    - Accessibility: when the user has `prefers-reduced-motion: reduce`
      we render a single static snapshot instead of animating.
    - Cleanup: all listeners + the rAF handle are torn down on unmount.

  No external dependencies — this works in any React+browser environment
  the rest of the dashboard already supports.
*/

import { useEffect, useRef } from "react";

/* ---------------------------------------------------------------- *
 * Tunables. Tweaking these is the fastest way to re-skin the feel  *
 * of the background without touching the render loop itself.       *
 * ---------------------------------------------------------------- */
const PARTICLE_COLORS = [
  "rgba(255, 255, 255, 1)",       // pure white
  "rgba(235, 244, 255, 1)",       // ice / very pale blue
  "rgba(140, 200, 255, 1)",       // cyan-blue accent
  "rgba(120, 175, 255, 1)",       // sky-blue accent
];

// Background gradient (top → bottom). Deep navy → almost-black blue.
const BG_GRADIENT = [
  { stop: 0.0, color: "#0a1a3a" },
  { stop: 0.55, color: "#061233" },
  { stop: 1.0, color: "#030a1f" },
];

/*
  Soft "aurora" glow blobs painted with a low-alpha radial gradient
  to add depth behind the particles. Their positions are fractions of
  the viewport so they reposition correctly on resize.
*/
const GLOW_BLOBS = [
  { xFrac: 0.18, yFrac: 0.25, radiusFrac: 0.55, color: "rgba(56, 130, 235, 0.18)" },
  { xFrac: 0.85, yFrac: 0.78, radiusFrac: 0.55, color: "rgba(120, 200, 255, 0.10)" },
  { xFrac: 0.55, yFrac: 0.42, radiusFrac: 0.50, color: "rgba(80, 150, 240, 0.08)" },
];

// One particle per N square pixels. Lower = denser. Capped further below.
const DENSITY_DIVISOR = 17000;
const MIN_PARTICLES = 45;
const MAX_PARTICLES = 130;

// Max distance (px) at which two particles are still connected by a line.
const LINK_DISTANCE = 130;

// Drift speed range (px / second).
const SPEED_MIN = 6;
const SPEED_MAX = 22;

// Radius range (px) for the particles themselves.
const RADIUS_MIN = 0.6;
const RADIUS_MAX = 2.4;

// Wobble — adds a soft horizontal sine motion so movement isn't linear.
const WOBBLE_AMPLITUDE = 0.7;   // px per frame, scaled by speed
const WOBBLE_FREQUENCY = 0.0009; // radians per ms

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function rand(min, max) {
  return min + Math.random() * (max - min);
}

/*
  Build the initial particle list. Particles drift mostly upward so the
  scene reads as "rising sparks" — the eye is drawn toward the centered
  sign-in card. A small fraction drift downward to keep the motion from
  looking too uniform.
*/
function createParticles(width, height) {
  const area = width * height;
  const count = Math.min(
    MAX_PARTICLES,
    Math.max(MIN_PARTICLES, Math.floor(area / DENSITY_DIVISOR))
  );
  const particles = [];
  for (let i = 0; i < count; i += 1) {
    const speed = rand(SPEED_MIN, SPEED_MAX);
    const angle = (-Math.PI / 2) + rand(-0.35, 0.35);     // mostly upward
    const direction = Math.random() < 0.85 ? 1 : -1;       // 15% drift down
    particles.push({
      x: Math.random() * width,
      y: Math.random() * height,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed * direction,
      radius: rand(RADIUS_MIN, RADIUS_MAX),
      color: pick(PARTICLE_COLORS),
      phase: Math.random() * Math.PI * 2,
      twinkle: rand(0.55, 1.0),
    });
  }
  return particles;
}

function paintBackground(ctx, width, height) {
  // 1) Vertical navy gradient.
  const grad = ctx.createLinearGradient(0, 0, 0, height);
  BG_GRADIENT.forEach((s) => grad.addColorStop(s.stop, s.color));
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, width, height);

  // 2) Soft aurora-style glow blobs for depth.
  const maxDim = Math.max(width, height);
  GLOW_BLOBS.forEach((b) => {
    const cx = b.xFrac * width;
    const cy = b.yFrac * height;
    const r = b.radiusFrac * maxDim;
    const blob = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    blob.addColorStop(0, b.color);
    blob.addColorStop(1, "rgba(0, 0, 0, 0)");
    ctx.fillStyle = blob;
    ctx.fillRect(0, 0, width, height);
  });

  // 3) Subtle vignette so the centered card has more visual weight.
  const vignette = ctx.createRadialGradient(
    width / 2,
    height / 2,
    Math.min(width, height) * 0.25,
    width / 2,
    height / 2,
    Math.max(width, height) * 0.75,
  );
  vignette.addColorStop(0, "rgba(0, 0, 0, 0)");
  vignette.addColorStop(1, "rgba(0, 0, 0, 0.45)");
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, width, height);
}

function paintLinks(ctx, particles) {
  /*
    Naive O(n²) neighbour search — fine for n ≲ 130. Each pair gets a
    single line with an alpha proportional to (1 - d/LINK_DISTANCE) so
    nearby pairs link strongly and the visual "constellation" fades out
    at the link radius instead of clipping.
  */
  const linkSq = LINK_DISTANCE * LINK_DISTANCE;
  ctx.lineWidth = 0.6;
  for (let i = 0; i < particles.length; i += 1) {
    const a = particles[i];
    for (let j = i + 1; j < particles.length; j += 1) {
      const b = particles[j];
      const dx = a.x - b.x;
      const dy = a.y - b.y;
      const dSq = dx * dx + dy * dy;
      if (dSq < linkSq) {
        const alpha = (1 - dSq / linkSq) * 0.35;
        ctx.strokeStyle = `rgba(180, 215, 255, ${alpha})`;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }
    }
  }
}

function paintParticles(ctx, particles, tMs) {
  for (let i = 0; i < particles.length; i += 1) {
    const p = particles[i];
    /*
      Each particle gets a soft "glow halo" via a radial gradient
      under a crisp solid core. The twinkle factor modulates the alpha
      with a phase-offset sine so the field looks alive without any
      particle disappearing entirely.
    */
    const tw = 0.6 + 0.4 * Math.sin(p.phase + tMs * 0.002);
    const haloR = p.radius * 6;
    const halo = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, haloR);
    halo.addColorStop(0, p.color.replace(", 1)", `, ${0.35 * p.twinkle * tw})`));
    halo.addColorStop(1, p.color.replace(", 1)", ", 0)"));
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(p.x, p.y, haloR, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = p.color.replace(", 1)", `, ${0.85 * p.twinkle * tw})`);
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
    ctx.fill();
  }
}

/*
  Step the particle simulation forward by `dt` seconds. We add a slow
  horizontal sine wobble so particles don't look like they're following
  rails. When a particle drifts out of the viewport we wrap it to the
  opposite edge instead of respawning so the count stays constant and
  the visual density never "blinks".
*/
function step(particles, width, height, dtSec, tMs) {
  for (let i = 0; i < particles.length; i += 1) {
    const p = particles[i];
    const wobble =
      Math.sin(tMs * WOBBLE_FREQUENCY + p.phase) * WOBBLE_AMPLITUDE;
    p.x += (p.vx + wobble) * dtSec;
    p.y += p.vy * dtSec;

    // Wrap-around so the constellation never thins out at edges.
    if (p.x < -10) p.x = width + 10;
    if (p.x > width + 10) p.x = -10;
    if (p.y < -10) p.y = height + 10;
    if (p.y > height + 10) p.y = -10;
  }
}

export default function ParticlesBackground() {
  const canvasRef = useRef(null);
  const stateRef = useRef({
    rafId: 0,
    lastT: 0,
    particles: [],
    width: 0,
    height: 0,
    dpr: 1,
    reduceMotion: false,
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) return undefined;

    const state = stateRef.current;

    const reduceMotionMql =
      typeof window !== "undefined" && window.matchMedia
        ? window.matchMedia("(prefers-reduced-motion: reduce)")
        : null;
    state.reduceMotion = !!reduceMotionMql?.matches;

    /*
      Resize handler: keeps the canvas pixel buffer in sync with the
      window in CSS pixels, multiplied by devicePixelRatio for crisp
      lines on retina displays. We rebuild the particle list on resize
      so density (particles per square pixel) stays roughly constant
      across very different viewport sizes.
    */
    const resize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      state.width = width;
      state.height = height;
      state.dpr = dpr;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      state.particles = createParticles(width, height);
      renderFrame(performance.now(), true);
    };

    const renderFrame = (tMs, forceStill = false) => {
      const { width, height, particles, reduceMotion } = state;
      const dt = state.lastT ? (tMs - state.lastT) / 1000 : 0;
      state.lastT = tMs;

      if (!reduceMotion && !forceStill) {
        // Cap dt so a backgrounded tab doesn't fling all particles
        // off-screen the moment the user returns to it.
        step(particles, width, height, Math.min(dt, 0.05), tMs);
      }

      paintBackground(ctx, width, height);
      paintLinks(ctx, particles);
      paintParticles(ctx, particles, tMs);
    };

    const loop = (tMs) => {
      renderFrame(tMs);
      state.rafId = window.requestAnimationFrame(loop);
    };

    const onMotionChange = (e) => {
      state.reduceMotion = !!e.matches;
    };

    resize();
    window.addEventListener("resize", resize);
    reduceMotionMql?.addEventListener?.("change", onMotionChange);

    if (state.reduceMotion) {
      // Static snapshot — no animation loop at all for accessibility.
      renderFrame(performance.now(), true);
    } else {
      state.rafId = window.requestAnimationFrame(loop);
    }

    return () => {
      window.cancelAnimationFrame(state.rafId);
      window.removeEventListener("resize", resize);
      reduceMotionMql?.removeEventListener?.("change", onMotionChange);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        display: "block",
        zIndex: -2,
        // GPU compositor hint — keeps the canvas on its own layer so it
        // doesn't repaint sibling DOM during the rAF loop.
        willChange: "transform",
        pointerEvents: "none",
        backgroundColor: BG_GRADIENT[BG_GRADIENT.length - 1].color,
      }}
    />
  );
}
